/**
 * useDropFollow — Hooks for Drop follow/unfollow.
 *
 * Drop-native peer of useHubFollow. Uses the generated DropFollowsApi client.
 * Drops MVP is always OPEN-mode follow — APPROVAL/INVITE_ONLY semantics live
 * on the Hub product and don't apply here.
 */

import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {useSelector} from "react-redux";
import {IStore} from "@/redux/defaultStore";
import {DropFollowsApi} from "@devour/client";
import getConfig from "@/utils/getConfig";

/**
 * Authenticated user's follow status for a Drop. Returns null when not logged in.
 */
export function useGetDropFollowStatus(slug?: string) {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);

    return useQuery({
        queryKey: ["drop-follow-status", slug, fullToken?.id ?? "unauthenticated"],
        queryFn: async () => {
            if (!fullToken?.token || !slug) return null;
            const api = new DropFollowsApi(getConfig(fullToken));
            return api.getDropsSlugIsFollowing({slug});
        },
        enabled: Boolean(slug && fullToken?.token),
        staleTime: 60000,
    });
}

interface FollowDropParams {
    slug: string;
    dropRef?: string;
}

/**
 * Follow a Drop. Returns the follow response with status (ACTIVE).
 * `dropRef` carries optional referrer attribution.
 */
export function useFollowDrop() {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (params: FollowDropParams) => {
            if (!fullToken?.token) throw new Error("Authentication required");
            const api = new DropFollowsApi(getConfig(fullToken));
            return api.createDropsSlugFollow({slug: params.slug, dropRef: params.dropRef});
        },
        onSuccess: (_data, params) => {
            queryClient.invalidateQueries({queryKey: ["drop-follow-status", params.slug]});
            queryClient.invalidateQueries({queryKey: ["drop", params.slug]});
            queryClient.invalidateQueries({queryKey: ["drop-followers", params.slug]});
        },
    });
}

/**
 * Unfollow a Drop. Idempotent.
 */
export function useUnfollowDrop() {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (slug: string) => {
            if (!fullToken?.token) throw new Error("Authentication required");
            const api = new DropFollowsApi(getConfig(fullToken));
            return api.deleteDropsSlugFollow({slug});
        },
        onSuccess: (_data, slug) => {
            queryClient.invalidateQueries({queryKey: ["drop-follow-status", slug]});
            queryClient.invalidateQueries({queryKey: ["drop", slug]});
            queryClient.invalidateQueries({queryKey: ["drop-followers", slug]});
        },
    });
}

/**
 * Paginated followers of a Drop. Public endpoint — no auth required.
 */
export function useGetDropFollowers(slug?: string, limit = 20, offset = 0) {
    return useQuery({
        queryKey: ["drop-followers", slug, limit, offset],
        queryFn: async () => {
            if (!slug) return null;
            const api = new DropFollowsApi(getConfig());
            return api.getDropsSlugFollowers({slug, limit, offset});
        },
        enabled: Boolean(slug),
        staleTime: 5 * 60000,
    });
}
